import { useEffect, useRef, useState } from "react";
import { Camera, X, Check, RotateCcw, Trash2, AlertTriangle } from "lucide-react";

// In-app multi-page camera. Take several photos of textbook/worksheet pages in one sitting;
// they are handed back to the parent to build ONE lesson. Rear camera on phones.
export function CameraCapture({ open, onClose, onDone }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [shots, setShots] = useState([]);
  const [error, setError] = useState("");
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!open) return;
    setShots([]);
    setError("");
    setReady(false);
    let cancelled = false;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" } }, audio: false,
        });
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
          setReady(true);
        }
      } catch (e) {
        console.debug("camera error:", e);
        setError("Couldn't open the camera. Check that your device has a camera and that you allowed camera access in your browser.");
      }
    })();
    return () => { cancelled = true; stopStream(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  const capture = () => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return;
    const canvas = document.createElement("canvas");
    canvas.width = v.videoWidth;
    canvas.height = v.videoHeight;
    canvas.getContext("2d").drawImage(v, 0, 0);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.9);
    setShots((s) => [...s, { url: dataUrl, b64: dataUrl.split(",")[1] }]);
  };

  const finish = () => {
    const n = shots.length;
    const files = shots.map((s, i) => ({
      name: `page-${i + 1}.jpg`, ext: "jpg", isImage: true, previewable: true, url: s.url, b64: s.b64,
    }));
    stopStream();
    onDone(files);
    onClose();
  };

  const cancel = () => { stopStream(); onClose(); };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[70] bg-black/90 flex flex-col" data-testid="camera-capture">
      <div className="flex items-center justify-between px-5 py-3 text-white">
        <span className="flex items-center gap-2 font-semibold"><Camera size={18} /> Snap pages</span>
        <button data-testid="camera-close" onClick={cancel} className="text-white/80 hover:text-white"><X size={22} /></button>
      </div>

      <div className="flex-1 grid place-items-center px-4 min-h-0">
        {error ? (
          <div className="max-w-sm text-center text-white/90 bg-white/10 rounded-2xl p-6">
            <AlertTriangle className="mx-auto mb-3 text-[#F4A261]" size={28} />
            <p className="text-sm">{error}</p>
            <p className="text-xs text-white/60 mt-3">Tip: you can also close this and use "Click to add files" to pick photos from your gallery.</p>
          </div>
        ) : (
          <video ref={videoRef} playsInline muted className="max-h-full max-w-full rounded-2xl bg-black" />
        )}
      </div>

      {shots.length > 0 && (
        <div className="px-4 py-2 flex gap-2 overflow-x-auto" data-testid="camera-shots">
          {shots.map((s, i) => (
            <div key={i} className="relative shrink-0 w-16 h-20 rounded-lg overflow-hidden border-2 border-white/30">
              <img src={s.url} alt={`page ${i + 1}`} className="w-full h-full object-cover" />
              <button onClick={() => setShots(shots.filter((_, x) => x !== i))} className="absolute top-0.5 right-0.5 bg-black/60 rounded-full p-0.5 text-white"><Trash2 size={11} /></button>
            </div>
          ))}
        </div>
      )}

      <div className="px-5 py-5 flex items-center justify-center gap-4 text-white">
        {shots.length > 0 && (
          <button data-testid="camera-retake" onClick={() => setShots(shots.slice(0, -1))} className="flex items-center gap-1.5 text-sm text-white/80 hover:text-white"><RotateCcw size={16} /> Undo last</button>
        )}
        <button data-testid="camera-capture" onClick={capture} disabled={!ready || !!error}
          className="w-16 h-16 rounded-full bg-white ring-4 ring-white/40 disabled:opacity-40 active:scale-95 transition-transform grid place-items-center">
          <span className="w-12 h-12 rounded-full bg-[#E07A5F]" />
        </button>
        <button data-testid="camera-done" onClick={finish} disabled={shots.length === 0}
          className="flex items-center gap-1.5 rounded-full bg-[#81B29A] px-5 py-2.5 text-sm font-semibold disabled:opacity-40">
          <Check size={16} /> Use {shots.length || ""} {shots.length === 1 ? "page" : "pages"}
        </button>
      </div>
    </div>
  );
}
